import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { CloseIcon, PinIcon, SendIcon } from './Icons';
import { STATUS_BADGE_CLASS } from './OrderQueryForm';

// Truly terminal — no further action possible from here at all. 'marked_done'
// is deliberately excluded: it's awaiting an approve/reject decision (see the
// TERMINAL_STATUSES.has(detail.status) branch below), not closed yet.
const TERMINAL_STATUSES = new Set(['resolved', 'rejected']);

function formatDate(raw) {
  if (!raw) return '-';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Matches the "Attachment (name): url" / "Attachment link: url" lines
// OrderQueryForm.js's submit folds into the query/message text (see
// server/ivr/routes.js's /erp/order-queries route - the ERP's OrderQuery
// model has no attachment field of its own, so this is the only place that
// carries one). Strips those lines out of the displayed text and returns
// them separately so they can be rendered as "View" buttons instead of a
// raw pasted link.
const ATTACHMENT_LINE_RE = /^Attachment(?: \(([^)]+)\))?(?: link)?:\s*(https?:\/\/\S+)$/i;
function extractAttachments(text) {
  const attachments = [];
  const cleanedLines = (text || '').split('\n').filter((line) => {
    const match = line.trim().match(ATTACHMENT_LINE_RE);
    if (!match) return true;
    attachments.push({ label: match[1] || '', url: match[2] });
    return false;
  });
  return { text: cleanedLines.join('\n').trim(), attachments };
}

function AttachmentLinks({ attachments }) {
  if (!attachments.length) return null;
  return (
    <div className="order-query-detail-attachments">
      {attachments.map((a, index) => (
        <a key={index} href={a.url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-small">
          View{a.label ? ` (${a.label})` : ''}
        </a>
      ))}
    </div>
  );
}

// Chat-style detail view for one order query - opened by clicking a row on
// OrderQueries.js. Mirrors the layout of the ERP's own order-queries admin
// page (query info grid, customer query, message thread, reply box, pin/
// mark-done actions) - see server/ivr/routes.js's /erp/order-queries/:id
// and erpClient.js's fetchOrderQueryDetail/addOrderQueryMessage/
// markOrderQueryDone/toggleOrderQueryPin. Always a live fetch/live actions
// against the ERP - nothing here is ever stored in the CRM, same principle
// as every other ERP-backed view in this app.
function OrderQueryDetail({ id, onClose, onUpdated }) {
  const { authFetch } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [markingDone, setMarkingDone] = useState(false);
  const [markDoneError, setMarkDoneError] = useState('');
  const [pinning, setPinning] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState('');
  // null = reject form closed; '' or partial text = form open, mirrors the
  // ERP frontend's own rejectReason state (null | string) for the same prompt.
  const [rejectReason, setRejectReason] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState('');

  const applyDetail = (next) => {
    setDetail(next);
    onUpdated?.(next);
  };

  const fetchDetail = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`/api/ivr/erp/order-queries/${encodeURIComponent(id)}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || 'Failed to load order query');
      applyDetail(body);
    } catch (err) {
      setError(err.message || 'Failed to load order query');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line
  }, [id]);

  const handleSend = async (event) => {
    event.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    setSendError('');
    try {
      const response = await authFetch(`/api/ivr/erp/order-queries/${encodeURIComponent(id)}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: newMessage.trim() }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || 'Failed to send message');
      applyDetail(body);
      setNewMessage('');
    } catch (err) {
      setSendError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleMarkDone = async () => {
    setMarkingDone(true);
    setMarkDoneError('');
    try {
      const response = await authFetch(`/api/ivr/erp/order-queries/${encodeURIComponent(id)}/mark-done`, {
        method: 'POST',
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || 'Failed to mark query done');
      applyDetail(body);
    } catch (err) {
      setMarkDoneError(err.message || 'Failed to mark query done');
    } finally {
      setMarkingDone(false);
    }
  };

  // Resolves a marked-done query — the CRM-side equivalent of the ERP
  // frontend's own "Approve" button (see server/ivr/routes.js's
  // /erp/order-queries/:id/approve and erpClient.js's approveOrderQuery).
  const handleApprove = async () => {
    setApproving(true);
    setApproveError('');
    try {
      const response = await authFetch(`/api/ivr/erp/order-queries/${encodeURIComponent(id)}/approve`, {
        method: 'POST',
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || 'Failed to approve query');
      applyDetail(body);
    } catch (err) {
      setApproveError(err.message || 'Failed to approve query');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason?.trim()) return;
    setRejecting(true);
    setRejectError('');
    try {
      const response = await authFetch(`/api/ivr/erp/order-queries/${encodeURIComponent(id)}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason.trim() }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || 'Failed to reject query');
      setRejectReason(null);
      applyDetail(body);
    } catch (err) {
      setRejectError(err.message || 'Failed to reject query');
    } finally {
      setRejecting(false);
    }
  };

  const handleTogglePin = async () => {
    setPinning(true);
    try {
      const response = await authFetch(`/api/ivr/erp/order-queries/${encodeURIComponent(id)}/toggle-pin`, {
        method: 'POST',
      });
      const body = await response.json();
      if (response.ok) applyDetail(body);
    } finally {
      setPinning(false);
    }
  };

  const terminal = detail ? TERMINAL_STATUSES.has(detail.status) : false;
  const awaitingApproval = detail?.status === 'marked_done';

  return (
    <div className="modal-overlay modal-overlay-center" onClick={onClose}>
      <div className="modal-panel modal-panel-narrow order-query-detail-panel" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="order-query-detail-eyebrow">Query Detail</p>
            <h2>{detail?.order_number || '-'}</h2>
          </div>
          <div className="modal-header-right">
            {detail && (
              <button
                type="button"
                className={`order-query-pin-btn ${detail.is_pinned ? 'active' : ''}`}
                onClick={handleTogglePin}
                disabled={pinning}
                aria-label={detail.is_pinned ? 'Unpin' : 'Pin'}
                title={detail.is_pinned ? 'Unpin' : 'Pin'}
              >
                <PinIcon width={14} height={14} />
              </button>
            )}
            {detail && (
              <span className={`${STATUS_BADGE_CLASS[detail.status] || 'status'} status-dot`}>{detail.status_label}</span>
            )}
            <button className="modal-close" onClick={onClose} aria-label="Close">
              <CloseIcon width={18} height={18} />
            </button>
          </div>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">Loading…</div>
          ) : error ? (
            <div className="call-feedback call-feedback-error">{error}</div>
          ) : (
            <>
              <div className="order-query-detail-info">
                <div>
                  <dt>Customer</dt>
                  <dd>{detail.customer_name || '-'}</dd>
                </div>
                <div>
                  <dt>Mobile</dt>
                  <dd>{detail.customer_mobile || '-'}</dd>
                </div>
                <div>
                  <dt>Order Number</dt>
                  <dd>{detail.order_number || '-'}</dd>
                </div>
                <div>
                  <dt>Query Type</dt>
                  <dd>{detail.query_type_label || detail.query_type || '-'}</dd>
                </div>
              </div>

              <div className="order-query-detail-section-label">Customer Query</div>
              {(() => {
                const { text: queryText, attachments: queryAttachments } = extractAttachments(detail.query);
                return (
                  <>
                    <div className="order-query-detail-quote">{queryText}</div>
                    <AttachmentLinks attachments={queryAttachments} />
                  </>
                );
              })()}

              <div className="order-query-detail-section-label">Query History</div>
              <div className="order-query-detail-thread">
                {(detail.messages || []).length === 0 ? (
                  <p className="modal-subtitle">No messages yet.</p>
                ) : (
                  detail.messages.map((m, index) => {
                    const { text: messageText, attachments: messageAttachments } = extractAttachments(m.message);
                    return (
                      <div key={index} className="order-query-detail-message">
                        <span className="order-query-detail-message-dot" />
                        <div className="order-query-detail-message-body">
                          <div className="order-query-detail-message-top">
                            <span className="order-query-detail-message-sender">{m.sender_name || 'System'}</span>
                            <span className="order-query-detail-message-time">{formatDate(m.created_at)}</span>
                          </div>
                          <div className="order-query-detail-message-text">{messageText}</div>
                          <AttachmentLinks attachments={messageAttachments} />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {terminal ? (
                <>
                  <p className="modal-subtitle">
                    This query is {detail.status_label?.toLowerCase() || 'closed'} — no further replies can be added here.
                  </p>
                  {detail.status === 'rejected' && detail.rejection_reason && (
                    <p className="modal-subtitle">
                      <strong>Reason:</strong> {detail.rejection_reason}
                    </p>
                  )}
                </>
              ) : awaitingApproval ? (
                // Awaiting a resolve/reject decision — the CRM-side equivalent of the
                // ERP frontend's own approve/reject step. Any logged-in CRM agent can
                // act here, same as raising/replying/mark-done; there's no separate
                // "admin" role on this side to gate it behind.
                <>
                  {rejectReason === null ? (
                    <div className="order-query-detail-actions">
                      <button type="button" className="btn btn-danger-outline" disabled={approving} onClick={() => setRejectReason('')}>
                        Reject
                      </button>
                      <button type="button" className="btn btn-primary" disabled={approving} onClick={handleApprove}>
                        {approving ? 'Approving…' : 'Approve'}
                      </button>
                    </div>
                  ) : (
                    <>
                      <textarea
                        className="order-query-detail-reject-textarea"
                        placeholder="Why is this being rejected?"
                        value={rejectReason}
                        onChange={(event) => setRejectReason(event.target.value)}
                        disabled={rejecting}
                      />
                      <div className="order-query-detail-actions">
                        <button type="button" className="btn btn-secondary" disabled={rejecting} onClick={() => setRejectReason(null)}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger-outline"
                          disabled={rejecting || !rejectReason.trim()}
                          onClick={handleReject}
                        >
                          {rejecting ? 'Rejecting…' : 'Confirm Reject'}
                        </button>
                      </div>
                    </>
                  )}
                  {approveError && <div className="call-feedback call-feedback-error">{approveError}</div>}
                  {rejectError && <div className="call-feedback call-feedback-error">{rejectError}</div>}
                </>
              ) : (
                <>
                  <form className="order-query-detail-reply" onSubmit={handleSend}>
                    <input
                      type="text"
                      className="order-query-detail-reply-input"
                      placeholder="Type a message…"
                      value={newMessage}
                      onChange={(event) => setNewMessage(event.target.value)}
                      disabled={sending}
                    />
                    <button
                      type="submit"
                      className="order-query-detail-send-btn"
                      disabled={sending || !newMessage.trim()}
                      aria-label="Send"
                    >
                      <SendIcon width={16} height={16} />
                    </button>
                  </form>
                  {sendError && <div className="call-feedback call-feedback-error">{sendError}</div>}

                  <div className="order-query-detail-actions">
                    <button type="button" className="btn btn-secondary" disabled={markingDone} onClick={handleMarkDone}>
                      {markingDone ? 'Marking…' : 'Query Solved'}
                    </button>
                  </div>
                  {markDoneError && <div className="call-feedback call-feedback-error">{markDoneError}</div>}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderQueryDetail;
